import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@staff-app/ui';
import {
  INTL_DEVICE_LABELS,
  INTL_ROUTE_LABELS,
  INTL_ROUTES,
  formatMinor,
  hasFlatFreightTable,
  isMcfServable,
  type IntlAllPricing,
  type IntlAllRequest,
} from '@staff-app/shared-types';
import {
  allocationsFromGrid,
  gridFromAllocations,
  routeAllowed,
  routeGridProblems,
  shipmentPreviews,
  type RouteEligibility,
  type RouteGrid,
} from '../route-grid';
import { useIntlAllInventory, useIntlAllMcfStock, useRouteIntlAllRequest } from '../hooks/useIntlAllRequests';

/**
 * ROUTE — fulfilment decides which route ships how many of each model (ROOT + FULFILLMENT).
 *
 * A model × route grid. A model may be split across routes; every model must add up EXACTLY to
 * what was requested (short is goods nobody ships, over is goods nobody ordered). China / Houston
 * freight is computed live from the shared flat table, the same function the invoice prices from.
 * MCF freight is Amazon's measured fee for the actual shipment, so it can only be asked once the
 * routing is saved — the dialog says so rather than guessing.
 */
export function RouteDialog({
  request,
  eligibility,
  pricing,
  open,
  onOpenChange,
}: {
  request: IntlAllRequest;
  eligibility: RouteEligibility;
  /** The current pricing, when routed — lets an UNCHANGED MCF share show Amazon's last figure. */
  pricing?: IntlAllPricing | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [grid, setGrid] = useState<RouteGrid>(() => gridFromAllocations(request.allocations));
  const [route, { loading, error }] = useRouteIntlAllRequest();

  const allocations = useMemo(() => allocationsFromGrid(grid), [grid]);
  const problems = useMemo(
    () => routeGridProblems(request.lines, allocations, eligibility, request.shipToCountry),
    [request.lines, allocations, eligibility, request.shipToCountry],
  );
  // Amazon MCF reaches only the US, the UK and the EU-27 (Paul, 2026-10-29); anywhere else ships
  // from China or Houston, so the MCF column is greyed out rather than refused on save.
  const mcfReachable = isMcfServable(request.shipToCountry);
  const previews = useMemo(() => shipmentPreviews(allocations, request.currency), [allocations, request.currency]);

  // Stock under each cell (Paul, 2026-10-29): China / Houston "In stock" from the ops inventory
  // sheet, less what
  // OTHER requests have routed there and not yet ordered; MCF live from Amazon. Advisory only — a
  // short cell turns amber, and Create order asks for a confirm, but routing is never refused.
  const { data: invData } = useIntlAllInventory(request.id, { skip: !open });
  const { data: mcfData } = useIntlAllMcfStock(request.id, { skip: !open || !mcfReachable });
  const stockFor = (model: string, r: string): { text: string; available: number | null; title?: string } | null => {
    if (r === 'MCF') {
      if (!mcfReachable) return null;
      const rows = (mcfData?.intlAllMcfStock ?? []).filter((x) => x.deviceModel === model);
      const hit = rows.find((x) => x.fulfillable != null);
      if (hit) return { text: `Amazon: ${hit.fulfillable}`, available: hit.fulfillable ?? null, title: hit.marketplaceLabel };
      if (!rows.length) return null;
      const testMode = /^Test mode/.test(rows[0].error ?? '');
      return { text: testMode ? 'Amazon: test mode' : 'Amazon: —', available: null, title: rows[0].error ?? undefined };
    }
    const level = invData?.intlAllInventory.find((x) => x.route === r && x.deviceModel === model);
    if (!level) return null;
    if (level.error) return { text: 'Sheet unavailable', available: null, title: level.error };
    if (level.onHand == null) return { text: 'Not on sheet', available: null };
    // The sheet's own number, as the team keeps it (Paul, 2026-10-29) — not less what other requests
    // have routed, which read as a wrong count. That figure is on hover, for context.
    return {
      text: `${level.onHand} in stock`,
      available: level.onHand,
      title: level.routedNotOrdered
        ? `${level.routedNotOrdered} also routed here on other requests not yet ordered`
        : undefined,
    };
  };

  // Amazon's figure is only valid for the MCF units it was asked about.
  const savedMcf = request.allocations
    .filter((a) => a.route === 'MCF')
    .map((a) => `${a.deviceModel}:${a.quantity}`)
    .sort()
    .join(',');
  const gridMcf = allocations
    .filter((a) => a.route === 'MCF')
    .map((a) => `${a.deviceModel}:${a.quantity}`)
    .sort()
    .join(',');
  const savedMcfFreight = pricing?.shipments.find((s) => s.route === 'MCF')?.freightMinor ?? null;

  const setCell = (model: string, r: string, value: string) =>
    setGrid((g) => ({ ...g, [model]: { ...(g[model as keyof RouteGrid] ?? {}), [r]: value } }));

  const save = async () => {
    if (problems.length || loading) return;
    try {
      await route({ variables: { input: { requestId: request.id, allocations } } });
      onOpenChange(false);
    } catch {
      // Surfaced from `error` below; the dialog stays open with the grid intact.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="air-route-dialog">
        <DialogHeader>
          <DialogTitle>{`Route ${request.requestNo}`}</DialogTitle>
          <DialogDescription>
            {'Choose how many of each device ships from each route.'}
          </DialogDescription>
        </DialogHeader>

        {mcfReachable ? null : (
          <p className="text-sm text-muted-foreground" data-testid="air-route-mcf-unreachable">
            {`Amazon MCF doesn't ship to ${request.shipToCountry}.`}
          </p>
        )}

        <table className="w-full text-sm" data-testid="air-route-grid">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2 font-medium">{'Device'}</th>
              <th className="py-1 pr-2 font-medium">{'Requested'}</th>
              {INTL_ROUTES.map((r) => (
                <th key={r} className="py-1 pr-2 font-medium">
                  {INTL_ROUTE_LABELS[r]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {request.lines.map((l) => (
              <tr key={l.deviceModel} className="border-t">
                <td className="py-2 pr-2">{INTL_DEVICE_LABELS[l.deviceModel] ?? l.deviceModel}</td>
                <td className="py-2 pr-2 tabular-nums">{l.quantity}</td>
                {INTL_ROUTES.map((r) => {
                  const outOfReach = r === 'MCF' && !mcfReachable;
                  const allowed = !outOfReach && routeAllowed(l.deviceModel, r, eligibility);
                  const value = grid[l.deviceModel]?.[r] ?? '';
                  return (
                    <td key={r} className="py-2 pr-2">
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        className="h-8 w-20"
                        value={value}
                        // A cell already holding a value stays editable even when the tick was
                        // removed since, so the operator can clear it; the problem list says why.
                        disabled={!allowed && !value}
                        title={
                          allowed
                            ? undefined
                            : outOfReach
                              ? `Amazon MCF does not ship to ${request.shipToCountry}`
                              : 'Not enabled for this route on the Products List'
                        }
                        aria-label={`${l.deviceModel} via ${INTL_ROUTE_LABELS[r]}`}
                        onChange={(e) => setCell(l.deviceModel, r, e.target.value)}
                        data-testid={`air-route-cell-${l.deviceModel}-${r}`}
                      />
                      {(() => {
                        const stock = allowed ? stockFor(l.deviceModel, r) : null;
                        if (!stock) return null;
                        const short = stock.available != null && Number(value || 0) > stock.available;
                        return (
                          <p
                            className={`mt-0.5 text-xs ${short ? 'font-medium text-amber-600' : 'text-muted-foreground'}`}
                            title={stock.title}
                            data-testid={`air-route-stock-${l.deviceModel}-${r}`}
                          >
                            {stock.text}
                          </p>
                        );
                      })()}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="air-route-freight">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide">{'Freight per shipment'}</p>
          {previews.length === 0 ? (
            <p className="text-muted-foreground">{'Nothing routed yet.'}</p>
          ) : (
            <ul className="space-y-1">
              {previews.map((p) => (
                <li key={p.route} data-testid={`air-route-freight-${p.route}`}>
                  <span className="font-medium">{INTL_ROUTE_LABELS[p.route]}</span>
                  {` · ${p.units} unit${p.units === 1 ? '' : 's'} · `}
                  {p.flatUsdMinor !== null
                    ? hasFlatFreightTable(request.currency)
                      ? `${formatMinor(p.flatUsdMinor, request.currency)} (flat table)`
                      : `${formatMinor(p.flatUsdMinor, 'usd')} (flat table, converted to ${request.currency.toUpperCase()} on the quote)`
                    : gridMcf === savedMcf && savedMcfFreight != null
                      ? `${formatMinor(savedMcfFreight, pricing?.currency ?? request.currency)} (Amazon's fee)`
                      : "Amazon's fee — priced once the routing is saved"}
                  {p.route === 'CHINA' ? ' · ships DDP, VAT included' : p.route === 'HOUSTON' ? ' · no VAT' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>

        {problems.length ? (
          <ul className="space-y-0.5 text-sm text-destructive" data-testid="air-route-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" data-testid="air-route-error">
            {error.message}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {'Cancel'}
          </Button>
          <Button onClick={save} disabled={problems.length > 0 || loading} data-testid="air-route-save">
            {loading ? 'Saving…' : 'Save routing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
